import type { BuildContext, Fragment } from "../types.js";
import { scaleFor } from "../format.js";
import { backgroundGradient } from "../primitives/backgroundGradient.js";
import { brandLogo } from "../primitives/brandLogo.js";
import { hookText } from "../primitives/hookText.js";
import { cta } from "../primitives/cta.js";
import { safeArea } from "../primitives/safeArea.js";
import { getLocale, type LocaleCode } from "../../locales/index.js";
import {
  patternInterruptSchema,
  type PatternInterruptProps,
} from "../../templates/index.js";
import { colors } from "../../brand/theme.js";
import { escapeHtml } from "../composition.js";
import { tl } from "../timeline.js";
import { assemble } from "./shared.js";

export const PATTERN_INTERRUPT_DURATION_SEC = 8;

export function buildPatternInterruptHtml(
  rawProps: unknown,
  ctx: Omit<BuildContext, "durationSec">,
): string {
  const props = patternInterruptSchema.parse(rawProps) as PatternInterruptProps;
  const loc = getLocale(ctx.locale as LocaleCode);
  const ctaText = props.cta || loc.ctas[props.ctaKey as keyof typeof loc.ctas];

  const fullCtx: BuildContext = { ...ctx, durationSec: PATTERN_INTERRUPT_DURATION_SEC };

  const stopSize = scaleFor(ctx.format, { base: 320, short: 400, square: 280, wide: 360 });
  const titleSize = scaleFor(ctx.format, { base: 84, short: 96, square: 76, wide: 90 });
  const payoffSize = scaleFor(ctx.format, { base: 56, short: 64, square: 50, wide: 60 });
  const ctaSize = scaleFor(ctx.format, { base: 50, short: 60, square: 46, wide: 54 });
  const logoSize = scaleFor(ctx.format, { base: 80, short: 100, square: 80, wide: 90 });

  // Sequences from PatternInterrupt:
  //   stop:        frame  0..36   (1.2s)
  //   followUp:    frame 36..176  (4.67s)
  //   payoff box:  frame 120..320 (6.67s — extends beyond comp)
  //   cta:         frame 170..240 (2.33s)
  const stopStart = 0;
  const stopDur = 36 / 30;
  const followStart = 36 / 30;
  const followDur = 140 / 30;
  const payoffStart = 120 / 30;
  const payoffDur = PATTERN_INTERRUPT_DURATION_SEC - payoffStart;
  const ctaStart = 170 / 30;
  const ctaDur = 70 / 30;

  // Flash overlay: white fading 0.9→0.5→0 over first 8 frames (0.267s)
  const flash: Fragment = {
    html: `<div id="tpl-flash" style="position:absolute;inset:0;background:white;pointer-events:none;z-index:5;"></div>`,
    init: `gsap.set("#tpl-flash", { opacity: 0.9 });`,
    tweens: [
      tl.to("#tpl-flash", { opacity: 0.5, duration: 4 / 30, ease: "linear" }, 0),
      tl.to("#tpl-flash", { opacity: 0, duration: 4 / 30, ease: "linear" }, 4 / 30),
    ],
  };

  const bg = backgroundGradient(fullCtx, "bg", { variant: "accent", animated: true });

  const logoFrag = brandLogo({ id: "tpl-logo", size: logoSize, color: "white" });

  // Shaking stop word — fade in over 4 frames then 6-frame x-shake loop.
  const stopHtml = `<div id="tpl-stop" style="font-weight:900;font-size:${stopSize}px;line-height:0.92;letter-spacing:-8px;color:white;text-shadow:0 18px 36px rgba(0,0,0,0.45);">${escapeHtml(props.stop)}</div>`;
  const stopFrag: Fragment = {
    html: stopHtml,
    init: `gsap.set("#tpl-stop", { opacity: 0, x: 0 });`,
    tweens: [
      tl.to("#tpl-stop", { opacity: 1, duration: 4 / 30, ease: "power1.out" }, 0),
      // Continuous x-shake while stop is visible: ~5 sin oscillations over 36 frames
      tl.to(
        "#tpl-stop",
        {
          x: 6,
          duration: 3 / 30,
          ease: "sine.inOut",
          repeat: Math.max(1, Math.floor((36 - 4) / 3)),
          yoyo: true,
        },
        4 / 30,
      ),
    ],
  };

  const followFrag = hookText({
    id: "tpl-follow",
    text: props.followUp,
    startSec: followStart,
    size: titleSize,
    color: "#fff",
    align: "left",
  });

  const payoffFrag: Fragment = {
    html: `<div id="tpl-payoff" style="margin-top:36px;font-weight:600;font-size:${payoffSize}px;color:white;background:${colors.bg}99;padding:28px;border-radius:24px;border:1px solid ${colors.accent};text-wrap:balance;">${escapeHtml(props.payoff)}</div>`,
    init: `gsap.set("#tpl-payoff", { opacity: 0, y: 24 });`,
    tweens: [
      tl.fromTo(
        "#tpl-payoff",
        { opacity: 0, y: 24 },
        { opacity: 1, y: 0, duration: 0.55, ease: "power2.out" },
        payoffStart,
      ),
    ],
  };

  const ctaFrag = cta({ id: "tpl-cta", text: ctaText, startSec: ctaStart, durationSec: ctaDur, size: ctaSize });

  const innerHtml = `
<div style="display:flex;justify-content:flex-start;">${logoFrag.html}</div>
<div style="flex:1;display:flex;flex-direction:column;justify-content:center;">
  <div id="tpl-stop-clip" class="clip" data-start="${stopStart}" data-duration="${stopDur}" data-track-index="0">${stopFrag.html}</div>
  <div id="tpl-follow-clip" class="clip" data-start="${followStart}" data-duration="${followDur}" data-track-index="1">${followFrag.html}</div>
  <div id="tpl-payoff-clip" class="clip" data-start="${payoffStart}" data-duration="${payoffDur}" data-track-index="2">${payoffFrag.html}</div>
</div>
<div id="tpl-cta-clip" class="clip" data-start="${ctaStart}" data-duration="${ctaDur}" data-track-index="3" style="display:flex;justify-content:flex-start;">${ctaFrag.html}</div>
`;

  const safe = safeArea({ id: "tpl-safe", format: ctx.format, childrenHtml: innerHtml });

  // Inline flash inside the stage HTML, BEFORE the safeArea but AFTER the bg
  // so the white flash is the topmost layer in the first ~0.27s.
  const bgWithFlash: Fragment = {
    html: bg.html + "\n" + flash.html,
    css: bg.css,
    init: [bg.init, flash.init].filter(Boolean).join("\n"),
    tweens: [...(bg.tweens ?? []), ...(flash.tweens ?? [])],
  };

  return assemble({
    templateId: "PatternInterrupt",
    ctx: fullCtx,
    background: bgWithFlash,
    contentFragments: [logoFrag, stopFrag, followFrag, payoffFrag, ctaFrag],
    safeAreaInnerHtml: innerHtml,
    safeArea: safe,
  });
}
